'use client';

import { memo, type ReactNode } from 'react';
import { Link } from '@/hen-ui/atomics/Link';
import { cn } from '@/hen-ui/utils';
import type { NavItem, SubNavColumnWidth, SubNavPanelStyle } from './types';
import { hasValidHref, isFeaturedColumn } from './utils';
import { NavColumn } from './NavColumn';
import { ThirdColumn } from './ThirdColumn';
import { FeaturedContentColumn } from './FeaturedContentColumn';
import { MenuContentSlot, useMenuContentSlot } from './MenuContentSlot';
import { FOCUS_RING } from './subnav.constants';
import {
  subNavColumnGapClass,
  subNavInlineOffsetClass,
  subNavInlineWrapClass,
  subNavPanelPaddingClass,
} from './subnav.variants';

const MENU_CONTENT_RAIL =
  'relative flex w-1/2 shrink-0 flex-col gap-md pl-lg before:absolute before:left-0 before:top-0 before:h-full before:w-px before:bg-neutral-200 xl:w-1/3';
const MENU_CONTENT_INLINE =
  'flex min-w-nav-menu-content flex-1 flex-col gap-md';
const PANEL_HEADING =
  'mt-xs text-b3 font-bold text-subtext lg:mt-xxs lg:text-b4 xl:mt-xs xl:text-h4';
const BELOW_HEADING = 'mt-lg lg:mt-sm xl:mt-lg';

interface DesktopContentPanelProps {
  heading: string;
  columns: NavItem[];
  dividedLastColumn?: boolean;
  /** Label of the top-level menu this panel belongs to. */
  menu?: string;
  /**
   * Authored content for the panel's side column. When content for `menu`
   * is present it replaces the featured or divided column. Inline content
   * replaces only the featured column; a divided column stays as a link
   * column.
   */
  menuContent?: ReactNode;
  /** When false, the heading above the columns is not rendered. */
  showHeading?: boolean;
  columnWidth?: SubNavColumnWidth;
  panelStyle?: SubNavPanelStyle;
}

function deriveColumns(regularColumns: NavItem[], dividedLastColumn: boolean) {
  const featured = regularColumns.find(isFeaturedColumn) ?? null;
  const ordinary = featured
    ? regularColumns.filter((col) => !isFeaturedColumn(col))
    : regularColumns;

  if (featured) {
    return {
      mainColumns: ordinary,
      dividerColumn: featured,
      isFeatured: true,
    } as const;
  }

  if (dividedLastColumn && ordinary.length > 1) {
    return {
      mainColumns: ordinary.slice(0, -1),
      dividerColumn: ordinary[ordinary.length - 1],
      isFeatured: false,
    } as const;
  }

  return {
    mainColumns: ordinary,
    dividerColumn: null,
    isFeatured: false,
  } as const;
}

function getColumnActionLinks(column: NavItem): NavItem[] {
  return (column.children ?? []).filter(
    (item) => item.type === 'action' && hasValidHref(item),
  );
}

function hasAnyActionLinks(columns: NavItem[]): boolean {
  return columns.some((col) => getColumnActionLinks(col).length > 0);
}

export const DesktopContentPanel = memo(function DesktopContentPanel({
  heading,
  columns,
  dividedLastColumn = false,
  menu = heading,
  menuContent,
  showHeading = true,
  columnWidth = 'fill',
  panelStyle,
}: DesktopContentPanelProps) {
  const { mainColumns, dividerColumn, isFeatured } = deriveColumns(
    columns,
    dividedLastColumn,
  );
  const menuContentSlot = useMenuContentSlot(menu);
  const hasMenuContentSlot = menuContent != null;
  const sideColumn = menuContentSlot.hasContent ? null : dividerColumn;
  const isInline =
    menuContentSlot.hasContent && menuContentSlot.placement === 'inline';
  const linkColumns =
    isInline && dividerColumn && !isFeatured
      ? [...mainColumns, dividerColumn]
      : mainColumns;

  const fixedColumns = columnWidth === 'fixed';
  const columnClassName = fixedColumns ? 'w-subnav-column shrink-0' : 'flex-1';
  const wrapsInline = isInline && fixedColumns;

  let mainClassName = 'w-full';
  if (sideColumn) {
    mainClassName = 'w-full xl:w-2/3 xl:pr-md';
  } else if (isInline) {
    mainClassName = fixedColumns ? 'shrink-0' : 'min-w-0 flex-1';
  } else if (hasMenuContentSlot) {
    mainClassName = menuContentSlot.hasContent
      ? 'min-w-0 flex-1 xl:pr-md'
      : 'min-w-0 flex-1';
  }

  return (
    <div className={subNavPanelPaddingClass(panelStyle)}>
      {isInline && showHeading ? (
        <div className={PANEL_HEADING}>{heading}</div>
      ) : null}
      <div
        className={cn(
          'flex flex-row',
          wrapsInline && subNavInlineWrapClass(panelStyle),
          isInline && showHeading && BELOW_HEADING,
        )}
      >
        <div
          className={mainClassName}
          style={
            isInline && !fixedColumns
              ? { flexGrow: Math.max(linkColumns.length, 1) }
              : undefined
          }
        >
          {showHeading && !isInline && (
            <div className={PANEL_HEADING}>{heading}</div>
          )}

          <div
            className={cn(
              'flex w-full flex-row',
              subNavColumnGapClass(panelStyle),
              showHeading && !isInline && BELOW_HEADING,
            )}
          >
            {linkColumns.map((column) => (
              <div key={column.id} className={columnClassName}>
                <NavColumn column={column} panelStyle={panelStyle} />
              </div>
            ))}
          </div>

          {hasAnyActionLinks(linkColumns) && (
            <div
              className={cn(
                'mt-lg flex flex-row py-xxs pr-xxs pl-xxs',
                // Fixed columns size the block to the columns, so the
                // padded row must not widen it.
                fixedColumns ? 'w-0 min-w-full' : 'w-full',
                subNavColumnGapClass(panelStyle),
              )}
            >
              {linkColumns.map((column) => {
                const actions = getColumnActionLinks(column);
                return (
                  <div key={column.id} className={columnClassName}>
                    {actions.map((item) => (
                      <Link
                        key={item.id}
                        href={item.uri}
                        variant='primary'
                        size='sm'
                        underline='none'
                        underlineStyle='thin'
                        className={`group inline-flex items-center gap-xs font-bold lg:gap-xxs xl:gap-xs ${FOCUS_RING}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {sideColumn &&
          (isFeatured ? (
            <FeaturedContentColumn column={sideColumn} />
          ) : (
            <ThirdColumn
              column={sideColumn}
              linkHover={panelStyle?.linkHover}
            />
          ))}

        {hasMenuContentSlot && (
          <MenuContentSlot
            value={menuContentSlot.value}
            capsInline
            className={
              isInline
                ? cn(
                    MENU_CONTENT_INLINE,
                    !wrapsInline && subNavInlineOffsetClass(panelStyle),
                  )
                : MENU_CONTENT_RAIL
            }
          >
            {menuContent}
          </MenuContentSlot>
        )}
      </div>
    </div>
  );
});
